import React, { useMemo } from 'react';

import DataGrid from '@gogame/data-grid';
import { SubscriptionCreate } from 'App/components/Subscriptions/SubscriptionsForm';
import { DateRangeFormatter } from '@gogame/intl-ts';

import {
  ApproverIcon,
  getApproverLabel,
} from 'scenes/FinancialResults/components/Approver';
import {
  Link,
  Stack,
  Typography,
  Tooltip,
  ErrorIcon,
  ErrorOutlineIcon,
  QueryBuilderIcon,
  InfoIcon,
} from '@gogame/components';
import { useChildComponent } from '@gogame/hooks-ts';
import {
  AmountFormatter,
  defaultRangePreset,
  NumberValueFormatter,
} from '@gogame/intl-ts';
import {
  useScreenState,
  useScreenDispatch,
  useScreenGo,
  useScreenTitle,
  ScreenLink,
} from '@gogame/screens';

import {
  FinancialResultFormEdit,
  FinancialResultFormDuplicate,
} from 'scenes/FinancialResults/components/FinancialResultForm';
import { FinancialResultFormCreate } from 'scenes/FinancialResults/components/FinancialResultForm';
import FinancialResultTitle from 'scenes/FinancialResults/components/FinancialResultTitle';
import { getColor } from 'scenes/FinancialResults/components/Status';

import {
  FR_PAGE,
  STATUS_DRAFT,
  TAB_ACTIONS_LOG,
  TAB_INVOICES,
  TAB_DETAILS,
  COLUMN_ID,
  COLUMN_PERIOD,
  COLUMN_EXPENSE_TYPE,
  COLUMN_APPLICATION,
  COLUMN_ROAS_D7,
  COLUMN_CPI,
  COLUMN_CVR,
  COLUMN_REGS,
  COLUMN_SPENDING,
  COLUMN_TOTAL_INVOICES_AMOUNT,
  COLUMN_AMOUNT_GAP,
  COLUMN_UA_REJECTED_INSTALLS,
  COLUMN_RE_REJECTED_INSTALLS,
  COLUMN_TOTAL_REJECTED_INSTALLS,
  COLUMN_AFFILIATES,
  COLUMN_AFFILIATE_IDS,
  COLUMN_BUSINESS_PARTNER,
  COLUMN_BUSINESS_PARTNER_ID,
  COLUMN_BUSINESS_PARTNER_COUNTRY,
  COLUMN_STATUS,
  COLUMN_OWNER,
  COLUMN_HISTORY_APPROVERS,
  COLUMN_IMPORTANCE,
  COLUMN_CREATED_DATE,
  COLUMN_LAST_UPDATED_DATE,
  COLUMN_PAYMENT_DATE,
  COLUMN_CUSTOMER_COMPANY,
  COLUMN_IO_BUDGET_ROR_FR,
  COLUMN_DUE_DATE,
  COLUMN_IO_DOCUMENT_PERIODS,
} from 'scenes/FinancialResults/constants';
import { useLoadFinancialResults } from 'scenes/FinancialResults/data';
import { useExportFinancialResults } from 'scenes/FinancialResults/data/mutations';
import {
  REORDER_COLUMNS,
  COLLAPSE_COLUMNS,
  SET_TABLE_SETTINGS,
} from 'scenes/FinancialResults/screenReducer';
import AffiliateDetails from 'scenes/Affiliates/components/AffiliateDetails';
import IODetails from 'scenes/IO/components/IODetails';
import BusinessPartnerDetails from 'scenes/BusinessPartners/components/BusinessPartnerDetails';

import { financialResultDetailsLink } from '../../navigation';
import Warnings from '../Warnings';
import BenchmarkTooltip from '../BenchmarkTooltip';
import { getTrackerParams } from './getTrackerParams';
import { trackerLink } from 'scenes/Trackers/Tracker/navigation';
import FrActions from '../Actions';
import FinancialResultsToolbar from './FinancialResultsToolbar';
import FinancialResulBulkActions from './FinancialResulBulkActions';
import FinancialResultsAllPagesBulkActions from './FinancialResultsAllPagesBulkActions';

const FinancialResultDetailsLink = ({ value }) => {
  return (
    <ScreenLink
      link={financialResultDetailsLink}
      pathParams={{
        id: value,
      }}
    >
      {value}
    </ScreenLink>
  );
};

const setPeriodType = (filter = {}) => {
  if (filter.periodType) {
    delete filter.periodType;
  }
  if (filter.createdPeriodType) {
    delete filter.createdPeriodType;
  }
  defaultRangePreset.forEach(({ key, value }) => {
    let periodEqual = 0;
    let createdPeriodEqual = 0;
    value.forEach((val, index) => {
      if (val === filter.period[index]) {
        periodEqual++;
      }
      if (periodEqual === value.length) {
        filter.periodType = key[0];
      }
      if (val === filter.created[index]) {
        createdPeriodEqual++;
      }
      if (createdPeriodEqual === value.length) {
        filter.createdPeriodType = key[0];
      }
    });
  });
};

const getRowId = ({ path }) => path;

const getTreeDataPath = ({ path }) => path.split('/');

const getParentNodes = (apiRef) =>
  apiRef.current
    .getAllRowIds()
    .map((id) => apiRef.current.getRowNode(id))
    .filter((rowNode) => rowNode.children && rowNode.children.length > 0);

const FinancialResultDataTable = () => {
  const [screenTitle] = useScreenTitle();
  const [childComponent, openChildComponent] = useChildComponent();
  const [exportFinancialResults] = useExportFinancialResults();
  const screenState = useScreenState();
  const dispatch = useScreenDispatch();
  const go = useScreenGo();

  const gridParams = useMemo(() => {
    const { filters, tableSettings, searchQuery } = screenState;
    setPeriodType(filters);
    return { filters, tableSettings, searchQuery };
  }, [screenState.filters, screenState.tableSettings, screenState.searchQuery]);

  const { data, isLoading, isRefetching } = useLoadFinancialResults(gridParams);

  const handleChangeSettings = (settings) => {
    dispatch({
      type: SET_TABLE_SETTINGS,
      payload: settings,
    });
  };

  const handleExport = async () => {
    await exportFinancialResults({
      ...gridParams,
      columns: screenState.columns,
    });
  };

  const handleReorder = (order) => {
    dispatch({
      type: REORDER_COLUMNS,
      payload: order,
    });
  };

  const handleCollapse = (collapsed) => {
    dispatch({
      type: COLLAPSE_COLUMNS,
      payload: collapsed,
    });
  };

  const handleEdit = (fr) => {
    openChildComponent(FinancialResultFormEdit, {
      name: <FinancialResultTitle {...fr} />,
      id: fr.id,
    });
  };

  const handleDuplicate = (fr) => {
    openChildComponent(FinancialResultFormDuplicate, {
      name: <FinancialResultTitle {...fr} />,
      id: fr.id,
    });
  };

  const handleViewLogs = (fr) => () => {
    go(fr.id, {
      state: { activeTabId: TAB_ACTIONS_LOG },
    });
  };

  const handleOpenSubscriptionsForm = () => {
    openChildComponent(SubscriptionCreate, {
      page: FR_PAGE,
      payload: { ...gridParams, columns: screenState.columns },
      withPayTo: false,
    });
  };

  const handleCreate = () => {
    openChildComponent(FinancialResultFormCreate, {
      onSubmit: (response) => {
        go(response.id);
      },
    });
  };

  const handleOpenBusinessPartner = (_, { id }) => {
    openChildComponent(BusinessPartnerDetails, {
      id: id,
    });
  };

  const handleOpenAffiliate = (_, { id }) => {
    openChildComponent(AffiliateDetails, {
      id: id,
    });
  };

  const handleOpenIo = (id) => {
    openChildComponent(IODetails, {
      id: id,
    });
  };

  const selectedRowsToolbarActions = ({ selectedValues, allPagesSelected }) => {
    return allPagesSelected ? (
      <FinancialResultsAllPagesBulkActions />
    ) : (
      <FinancialResulBulkActions selectedValues={selectedValues} />
    );
  };

  const apiRef = React.useRef(null);

  const [allExpanded, setAllExpanded] = React.useState(false);

  React.useEffect(() => {
    if (apiRef.current) {
      return apiRef.current?.subscribeEvent('rowExpansionChange', () => {
        const _allExpanded = getParentNodes(apiRef).every(
          (rowNode) => rowNode.childrenExpanded
        );

        setAllExpanded(_allExpanded);
      });
    }
  }, [apiRef.current]);

  const handleToggleAll = () => {
    getParentNodes(apiRef).forEach((rowNode) => {
      apiRef.current.setRowChildrenExpansion(rowNode.id, !allExpanded);
    });
  };

  return (
    <React.Fragment>
      {childComponent}
      <DataGrid
        apiRef={apiRef}
        selectedRowsToolbarActions={selectedRowsToolbarActions}
        checkboxSelection={true}
        isRowSelectable={isParentRow}
        allPageSelection={true}
        rows={data?.rows}
        count={data?.count}
        title={screenTitle}
        loading={isLoading}
        fetching={isRefetching}
        ready={screenState.ready}
        onChangeSettings={handleChangeSettings}
        onReorder={handleReorder}
        onCollapse={handleCollapse}
        tableSettings={screenState.tableSettings}
        columnsState={screenState.columns}
        treeDataWithGaps={true}
        toolbar={
          <FinancialResultsToolbar
            onCreate={handleCreate}
            onExport={handleExport}
            onOpenSubscriptionsForm={handleOpenSubscriptionsForm}
            onToggleAll={handleToggleAll}
            allExpanded={allExpanded}
          />
        }
        storageKey={'financialResults'}
        defaultColumnWidth={120}
        filtered={!!screenState.searchQuery}
        getRowId={getRowId}
        treeData={true}
        getTreeDataPath={getTreeDataPath}
      >
        <DataGrid.Column
          field={COLUMN_ID}
          type={'link'}
          headerName={'ID'}
          align={'right'}
          headerAlign={'right'}
          width={80}
          fixed={true}
          resizable={false}
          linkComponent={FinancialResultDetailsLink}
        />

        <DataGrid.Column
          field={COLUMN_PERIOD}
          type={'dateRange'}
          headerName={'Period'}
          width={128}
          template={'MONTH'}
        />

        <DataGrid.Column
          field={COLUMN_DUE_DATE}
          headerName={'Due Date'}
          width={110}
          type={'date'}
        />
        <DataGrid.Column
          field={COLUMN_BUSINESS_PARTNER}
          headerName={'Business Partner'}
          width={220}
          type={'link'}
          onLinkClick={handleOpenBusinessPartner}
          linkComponent={({ value, onClick }) => {
            const { name, warnings } = value;
            return (
              <Warnings warnings={warnings} tooltip={name}>
                <Link onClick={(_) => onClick(_, value)}>{name}</Link>
              </Warnings>
            );
          }}
        />
        <DataGrid.Column
          field={COLUMN_BUSINESS_PARTNER_ID}
          headerName={'BP Id'}
          width={80}
          type={'text'}
        />
        <DataGrid.Column
          field={COLUMN_AFFILIATES}
          headerName={'Affiliates'}
          limit={1}
          width={220}
          type={'list'}
          placeholder={'All'}
          unitsLabel={'Affiliates'}
          onClickLink={handleOpenAffiliate}
          getItemValue={(v, colDef) => {
            const { onClickLink } = colDef;
            const { name } = v;
            return <Link onClick={(_) => onClickLink(_, v)}>{name}</Link>;
          }}
        />
        <DataGrid.Column
          field={COLUMN_AFFILIATE_IDS}
          type={'commaList'}
          width={80}
          headerName={'Affiliate Ids'}
          getItemValue={({ id }) => id}
        />
        <DataGrid.Column
          field={COLUMN_BUSINESS_PARTNER_COUNTRY}
          headerName={'BP County'}
          limit={1}
          width={140}
          getValue={(v) => v?.name}
          type={'text'}
        />
        <DataGrid.Column
          field={COLUMN_STATUS}
          headerName={'Status'}
          type={'status'}
          width={132}
          getStatusColor={getColor}
        />
        <DataGrid.Column
          field={COLUMN_IMPORTANCE}
          headerName={'Importance'}
          type={'text'}
          getTooltip={(v) => {
            return v?.name;
          }}
          getIcon={(v) => {
            return v?.id === 1 ? (
              <InfoIcon sx={{ color: '#EC7D05', fontSize: 16 }} />
            ) : null;
          }}
          getValue={(v) => v?.name}
          width={140}
          getStatusColor={getColor}
        />
        <DataGrid.Column
          field={COLUMN_CUSTOMER_COMPANY}
          headerName={'Customer Company'}
          getValue={(v) => v?.name}
          type={'text'}
          width={220}
        />
        <DataGrid.Column
          field={COLUMN_EXPENSE_TYPE}
          headerName={'Expense Type'}
          getValue={(v) => v?.name}
          type={'text'}
          width={100}
        />

        <DataGrid.Column
          headerName={'Applications'}
          type={'text'}
          field={COLUMN_APPLICATION}
          getValue={(v) => v?.name || '-'}
          sortable={false}
          width={240}
          groupingColDef
        />

        <DataGrid.Column
          field={COLUMN_ROAS_D7}
          headerName={'Total ROAS D7'}
          type={'text'}
          align={'right'}
          headerAlign={'right'}
          getTooltip={() => {}}
          formatter={(v, row) => {
            return (
              <BenchmarkTooltip
                {...row.applicationBenchmark}
                businessEntities={row.businessEntities}
              >
                <NumberValueFormatter.Percent value={v} />
              </BenchmarkTooltip>
            );
          }}
          width={110}
        />
        <DataGrid.Column
          field={COLUMN_CPI}
          headerName={'CPI'}
          type={'amount'}
          width={72}
        />
        <DataGrid.Column
          field={COLUMN_CVR}
          headerName={'CVR'}
          type={'percent'}
          width={72}
        />

        <DataGrid.Column
          field={COLUMN_REGS}
          headerName={'Regs'}
          type={'number'}
          width={72}
        />

        <DataGrid.Column
          field={COLUMN_SPENDING}
          headerName={'Spend'}
          type={'__custom'}
          width={110}
          align={'right'}
          headerAlign={'right'}
          formatter={(v, row) => {
            const initialSpending = row.initialSpending;
            const rejectedSpending = row.rejectedSpending;

            const trackerParams =
              row?.status?.id !== STATUS_DRAFT ? getTrackerParams(row) : null;

            return (
              <Stack style={{ minWidth: 0 }}>
                {rejectedSpending && rejectedSpending.amount > 0 ? (
                  <Tooltip
                    title={
                      <NumberValueFormatter.Currency
                        value={rejectedSpending.amount}
                        currency={rejectedSpending.currencyCode}
                      />
                    }
                    wrapper
                  >
                    <ErrorOutlineIcon sx={{ fontSize: 12 }} color={'error'} />
                  </Tooltip>
                ) : null}
                {trackerParams ? (
                  <Typography varinat={'body2'} noWrap as={'span'}>
                    <ScreenLink
                      link={trackerLink}
                      params={trackerParams}
                      target={'_blank'}
                      noWrap={true}
                    >
                      <Tooltip
                        wrapper={true}
                        title={
                          'Spend for the activity covered by this FR as it was at the moment of the financial result creation. Click the link to go to the advertising tracker to see the actual Spend'
                        }
                      >
                        <AmountFormatter {...v} style={{ color: 'inherit' }} />
                      </Tooltip>
                    </ScreenLink>
                  </Typography>
                ) : (
                  <AmountFormatter {...v} />
                )}
                <Stack
                  direction={'row'}
                  spacing={0.5}
                  alignItems={'center'}
                  justifyContent={'flex-end'}
                >
                  <Tooltip title={'Original spend'}>
                    <span style={{ lineHeight: 0 }}>
                      <QueryBuilderIcon
                        sx={{ fontSize: 12 }}
                        color={'action'}
                      />
                    </span>
                  </Tooltip>
                  <Typography variant={'caption'}>
                    <NumberValueFormatter.Currency
                      value={initialSpending.amount}
                      currency={initialSpending.currencyCode}
                    />
                  </Typography>
                </Stack>
              </Stack>
            );
          }}
        />

        <DataGrid.Column
          field={COLUMN_TOTAL_INVOICES_AMOUNT}
          headerName={'Total Invoices Amount'}
          type={'text'}
          getIcon={(_, row) => {
            return row.isBudgetOverrun ? (
              <Tooltip title={'Exceeds IO monthly budget'}>
                <span style={{ lineHeight: 0 }}>
                  <ErrorIcon color={'error'} sx={{ fontSize: 16 }} />
                </span>
              </Tooltip>
            ) : null;
          }}
          align={'right'}
          headerAlign={'right'}
          width={130}
          getTooltip={() => {}}
          formatter={(v, row) =>
            v ? (
              <ScreenLink
                link={financialResultDetailsLink}
                pathParams={{
                  id: `${row.id}`,
                }}
                state={{
                  activeTabId:
                    row?.status?.id === STATUS_DRAFT
                      ? TAB_DETAILS
                      : TAB_INVOICES,
                }}
              >
                <AmountFormatter {...v} vertical={true} />
              </ScreenLink>
            ) : null
          }
        />
        <DataGrid.Column
          field={COLUMN_AMOUNT_GAP}
          headerName={'Amount Gap'}
          width={92}
          type={'__custom'}
          align={'right'}
          headerAlign={'right'}
          getTooltip={(v) => {
            if (v) {
              return (
                <NumberValueFormatter.Currency
                  value={v.amount}
                  currency={v.currencyCode}
                />
              );
            }
          }}
          formatter={(v, row) => {
            if (!v) {
              return null;
            }
            return (
              <Stack direction={'column'} style={{ minWidth: 0 }}>
                <Typography variant={'body2'} noWrap as={'span'}>
                  <NumberValueFormatter.Currency
                    value={v.amount}
                    currency={v.currencyCode}
                  />
                </Typography>
                {row.amountGapPercent ? (
                  <span>
                    <NumberValueFormatter.Percent
                      value={row.amountGapPercent}
                    />
                  </span>
                ) : null}
              </Stack>
            );
          }}
        />
        <DataGrid.Column
          field={COLUMN_UA_REJECTED_INSTALLS}
          headerName={'UA Rejected Installs by post-attribution fraud'}
          width={180}
          type={'number'}
        />
        <DataGrid.Column
          field={COLUMN_RE_REJECTED_INSTALLS}
          headerName={'Re Rejected Installs by post-attribution fraud'}
          width={180}
          type={'number'}
        />
        <DataGrid.Column
          field={COLUMN_TOTAL_REJECTED_INSTALLS}
          headerName={'Total Rejected Installs by post-attribution fraud'}
          width={180}
          type={'number'}
        />
        <DataGrid.Column
          field={COLUMN_HISTORY_APPROVERS}
          headerName={'Approvers'}
          width={160}
          type={'list'}
          limit={1}
          getItemIcon={ApproverIcon}
          getItemValue={getApproverLabel}
          getItemTooltip={getApproverLabel}
          sortable={false}
        />
        <DataGrid.Column
          field={COLUMN_PAYMENT_DATE}
          headerName={'Payment Date'}
          width={110}
          type={'date'}
          template={'LLL'}
          timeZone={'local'}
        />
        <DataGrid.Column
          field={COLUMN_IO_DOCUMENT_PERIODS}
          headerName={'IO periods'}
          width={180}
          type={'list'}
          limit={1}
          sortable={false}
          onOpenIo={handleOpenIo}
          getItemTooltip={(v) => {
            const { from, to } = v.period;
            return <DateRangeFormatter value={[from, to]} template={'MONTH'} />;
          }}
          getItemValue={(v, colDef) => {
            const { from, to } = v.period;
            return (
              <Link onClick={() => colDef.onOpenIo(v.id)}>
                <DateRangeFormatter value={[from, to]} template={'MONTH'} />
              </Link>
            );
          }}
        />

        <DataGrid.Column
          field={COLUMN_IO_BUDGET_ROR_FR}
          headerName={'IO Budget for FR period'}
          width={110}
          type={'amount'}
        />
        <DataGrid.Column
          field={COLUMN_LAST_UPDATED_DATE}
          headerName={'Last Updated'}
          width={160}
          type={'date'}
          template={'LLL'}
          timeZone={'local'}
        />
        <DataGrid.Column
          field={COLUMN_OWNER}
          getValue={(v) => v?.name}
          headerName={'Created by'}
          width={160}
          type={'text'}
        />
        <DataGrid.Column
          field={COLUMN_CREATED_DATE}
          headerName={'Created'}
          width={160}
          type={'date'}
          template={'LLL'}
        />
        <DataGrid.Column
          actionsColDef
          type={'actions'}
          limit={3}
          withViewLog={true}
          onViewLog={handleViewLogs}
          onDuplicate={handleDuplicate}
          onEdit={handleEdit}
          actionsComponent={FrActions}
          isActionVisible={isParentRow}
        />
      </DataGrid>
    </React.Fragment>
  );
};

function isParentRow({ row }) {
  return row.path.split('/').length === 1;
}

export default FinancialResultDataTable;
